import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { leads, events } from "../../common/db";
import { InvalidEventModal, WarningModal } from "../../common/modal";
import {
  addSponsorLead,
  getSponsorLeads,
  updateSponsorLead,
} from "../../common/api";
import { useLiveQuery } from "dexie-react-hooks";
import { onCreateSponsorLead } from "../../graphql/subscriptions";
import Dexie from "dexie";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useToast } from "../../design-system/contexts/ToastContext";
import { safeJSONParse, safeJSONStringify, Dubai, shouldShowTeamsFeature } from "../../common/functions";
import { Scanner } from "@yudiel/react-qr-scanner";
import AuthContext from "../../contexts/AuthContext";
import { Icon } from "@iconify/react";
import { BottomNavbar } from "../../components/BottomNavbar";
import MenuActionsModal from "../../components/MenuActionsModal/MenuActionsModal";
import ProfileModal from "../../components/ProfileModal";
import ChromeWarningBanner from "../../components/ChromeWarningBanner";
import { useTheme } from "../../design-system/contexts/ThemeContext";
import { Avatar } from "../../design-system";
import SecondaryNav from "../../components/SecondaryNav";
import DesktopHeader from "../../components/DesktopHeader";
import { useIOSKeyboard } from "../../hooks";
import "./ScanDetails.css";

const ScanDetails = () => {
  // ===== State =====
  const [invalidEventModal, setInvalidEventModal] = useState(false);
  const [booking, setBooking] = useState("");
  const [commentedText, setCommentedText] = useState("");
  const [error, setError] = useState("");
  const [display, setDisplay] = useState(true);
  const [cancel, setCancel] = useState(false);
  const [result, setResult] = useState("");
  const [invalidQrCode, setInvalidQrCode] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);
  const [duplicateLeadModal, setDuplicateLeadModal] = useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [cameraBlockedModal, setCameraBlockedModal] = useState(false);
  const [eventDetails, setEventDetails] = useState([]);
  const [cameraError, setCameraError] = useState(false);
  const [activeTab, setActiveTab] = useState("scan"); // "scan" or "manual"
  // Detect iPad specifically (even in landscape/desktop mode)
  // Modern iPads may report as MacIntel with touch points, or have iPad in userAgent
  const isIPad = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const hasTouchPoints = navigator.maxTouchPoints > 1;
    const isIPadUA = /iPad/.test(ua);
    const isIPadPlatform = platform === 'MacIntel' && hasTouchPoints;
    return isIPadUA || isIPadPlatform;
  }, []);

  // isDesktop should be FALSE on iPad, even in landscape mode
  // iPad should use mobile UI (hamburger menu, bottom nav, etc.)
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof navigator === 'undefined') return window.innerWidth >= 992;
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const hasTouchPoints = navigator.maxTouchPoints > 1;
    const isIPadDevice = /iPad/.test(ua) || (platform === 'MacIntel' && hasTouchPoints);
    return window.innerWidth >= 992 && !isIPadDevice;
  });
  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [scannerKey, setScannerKey] = useState(0); // Key to force scanner remount

  const Context = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const { error: showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { isKeyboardOpen, keyboardHeight } = useIOSKeyboard();
  const [isAndroidKeyboardOpen, setIsAndroidKeyboardOpen] = useState(false);

  // ===== URL Parsing =====
  let fetch_url = window.location.href;
  // Handle both /events/ and /Events/ URL formats
  const eventsIndex = fetch_url.toLowerCase().indexOf("events/");
  let event_id = fetch_url.substring(
    eventsIndex + 7,
    fetch_url.lastIndexOf("/scanDetails")
  );

  let fetch_url_search = window.location.search;
  const urlParams = new URLSearchParams(fetch_url_search);

  let sponsor_code = urlParams.get("sponsor")
    ? urlParams.get("sponsor")
    : localStorage.getItem("sponsorCode");
  let validEmailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

  const localProfileName = localStorage.getItem("profileName");

  const profileName =
    localProfileName && localProfileName !== "undefined"
      ? safeJSONParse(localProfileName)
      : Context?.state?.user?.attributes?.name ||
        Context?.state?.user?.attributes?.email ||
        "reachus@konfhub.com";

  const today = new Date();
  let currentTime = today.getTime();
  let data = [];
  let loop = false;
  let leadList = [];
  const shouldIgnoreKeyboardHide = isIPad && isLandscape;
  const shouldApplyKeyboardLayout = (isKeyboardOpen || isAndroidKeyboardOpen) && !shouldIgnoreKeyboardHide;

  // ===== Effects =====
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      // iPad should always use mobile UI regardless of screen width
      if (isIPad) {
        setIsDesktop(false);
        return;
      }
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isIPad]);

  // Listen for scan button click when already on scan page
  useEffect(() => {
    const handleSwitchToScanTab = () => {
      setActiveTab("scan");
      // Reset scanner state when switching to scan tab
      setDisplay(false);
      setCameraError(false);
      setScannerKey(prev => prev + 1);
    };

    window.addEventListener('switchToScanTab', handleSwitchToScanTab);
    return () => {
      window.removeEventListener('switchToScanTab', handleSwitchToScanTab);
    };
  }, []);

  // Reset scanner when navigating back to scan page
  useEffect(() => {
    // Check if we're on the base scan page (not in a modal route)
    const isOnBaseScanPage = location.pathname.endsWith("/scanDetails") || 
                             (location.pathname.includes("/scanDetails") && 
                              !location.pathname.includes("/ScanLeadModal") &&
                              !location.pathname.includes("/DetailsCardModal") &&
                              !location.pathname.includes("/ShareSponsorCode"));
    
    if (isOnBaseScanPage && activeTab === "scan") {
      // Reset display state and remount scanner when coming back from a modal
      // This ensures the scanner reinitializes properly and camera stream restarts
      setDisplay(false);
      setCameraError(false);
      // Force scanner remount by incrementing key
      setScannerKey(prev => prev + 1);
    }
  }, [location.pathname, activeTab]);

  // Preload offline banner icons when component mounts (for offline support)
  useEffect(() => {
    // Preload icons by rendering them off-screen when online
    if (navigator.onLine) {
      const preloadContainer = document.createElement('div');
      preloadContainer.style.position = 'absolute';
      preloadContainer.style.left = '-9999px';
      preloadContainer.style.opacity = '0';
      preloadContainer.style.pointerEvents = 'none';
      document.body.appendChild(preloadContainer);
      
      // Create temporary icons to force load
      const wifiIcon = document.createElement('div');
      wifiIcon.innerHTML = '<iconify-icon icon="bx:wifi-off" width="30" height="30" style="color: #FFFFFF"></iconify-icon>';
      preloadContainer.appendChild(wifiIcon);
      
      const closeIcon = document.createElement('div');
      closeIcon.innerHTML = '<iconify-icon icon="mdi:close" width="18" height="18" style="color: #FFFFFF"></iconify-icon>';
      preloadContainer.appendChild(closeIcon);
      
      // Clean up after a delay
      setTimeout(() => {
        if (preloadContainer.parentNode) {
          document.body.removeChild(preloadContainer);
        }
      }, 5000);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    fetchEventDetails();
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (networkStatus) {
      if (offlineData) {
        syncDetailsAPI();
      } else {
        getSponsorLeadsApi();
      }
      setRefreshDisabled(false);
    } else {
      setRefreshDisabled(true);
    }
  }, [networkStatus]);

  // Android keyboard detection
  useEffect(() => {
    const isAndroid = /Android/.test(navigator.userAgent);
    if (!isAndroid) return;

    let initialHeight = window.innerHeight;
    const viewport = window.visualViewport;

    const handleResize = () => {
      if (!viewport) {
        // Fallback for browsers without visualViewport
        const currentHeight = window.innerHeight;
        const heightDiff = initialHeight - currentHeight;
        setIsAndroidKeyboardOpen(heightDiff > 150);
      } else {
        const heightDiff = initialHeight - viewport.height;
        setIsAndroidKeyboardOpen(heightDiff > 150);
      }
    };

    const handleViewportResize = () => {
      if (viewport) {
        const heightDiff = initialHeight - viewport.height;
        setIsAndroidKeyboardOpen(heightDiff > 150);
      }
    };

    window.addEventListener('resize', handleResize);
    if (viewport) {
      viewport.addEventListener('resize', handleViewportResize);
    }

    // Also detect on input focus/blur
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        setTimeout(() => {
          const currentHeight = viewport ? viewport.height : window.innerHeight;
          const heightDiff = initialHeight - currentHeight;
          setIsAndroidKeyboardOpen(heightDiff > 150);
        }, 300);
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        setIsAndroidKeyboardOpen(false);
        initialHeight = window.innerHeight;
      }, 100);
    };

    document.addEventListener('focusin', handleFocus, true);
    document.addEventListener('focusout', handleBlur, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (viewport) {
        viewport.removeEventListener('resize', handleViewportResize);
      }
      document.removeEventListener('focusin', handleFocus, true);
      document.removeEventListener('focusout', handleBlur, true);
    };
  }, []);

  useEffect(() => {
    const createSubscription = API.graphql(
      graphqlOperation(onCreateSponsorLead)
    ).subscribe({
      next: async (updateData) => {
        let subscriptionRes = safeJSONParse(
          updateData.value.data.onCreateSponsorLead
        );
        if (subscriptionRes.sponsorCode == sponsor_code) {
          const fetchEventDetailsFromDBDET = await events
            .where("eventId")
            .equals(event_id)
            .toArray();

          let fetchAttendeeDetailsFromDB = [];
          if (fetchEventDetailsFromDBDET.city) {
            fetchAttendeeDetailsFromDB = await leads
              .where("detCode")
              .equals(subscriptionRes.detCode)
              .toArray();
          } else {
            // Get ALL leads for this event and match by any possible combination
            // This is more reliable than individual queries
            const allEventLeads = await leads.where("eventId").equals(event_id).toArray();
            const normalizedBookingId = subscriptionRes.bookingId?.toLowerCase();
            const normalizedEmailId = subscriptionRes.emailId?.toLowerCase();
            
            // Try to find by multiple criteria - check all possible matches
            fetchAttendeeDetailsFromDB = allEventLeads.filter(lead => {
              const leadBookingId = lead.bookingId?.toLowerCase();
              const leadId = lead.id?.toLowerCase();
              const leadEmailId = lead.emailId?.toLowerCase();
              
              // Match by bookingId (in id or bookingId field)
              if (normalizedBookingId && (
                leadId === normalizedBookingId || 
                leadBookingId === normalizedBookingId
              )) {
                return true;
              }
              
              // Match by emailId
              if (normalizedEmailId && (
                leadEmailId === normalizedEmailId ||
                leadId === normalizedEmailId ||
                leadBookingId === normalizedEmailId
              )) {
                return true;
              }
              
              return false;
            });
          }

          // Only process if eventId matches
          if (subscriptionRes.eventId !== event_id) {
            return; // Skip if not for this event
          }

          if (fetchAttendeeDetailsFromDB.length > 0) {
            // Update existing lead with correct name from server if it differs
            const existingLead = fetchAttendeeDetailsFromDB[0];
            
            // PROTECT manually saved leads - don't let subscription overwrite them for 5 seconds
            const isRecentlyManualSaved = existingLead._manualSave && existingLead._savedAt && 
                                         (Date.now() - existingLead._savedAt) < 5000;
            
            if (isRecentlyManualSaved) {
              // Skip subscription update for recently manually saved leads
              // This prevents subscription from interfering with manual saves
              return;
            }
            
            // Only update name if the new name is valid (not empty, not "Unknown")
            // and either the current name is invalid (same as bookingId/id/emailId) or the new name is different
            const newName = subscriptionRes.name?.trim();
            const isValidNewName = newName && newName !== "" && newName.toLowerCase() !== "unknown";
            const currentNameIsInvalid = existingLead.name === existingLead.bookingId ||
                                       existingLead.name === existingLead.id ||
                                       (existingLead.emailId && existingLead.name === existingLead.emailId) ||
                                       !existingLead.name?.trim() ||
                                       existingLead.name?.trim().toLowerCase() === "unknown";
            
            // NEVER update to an invalid name if current name is valid
            if (isValidNewName && (currentNameIsInvalid || existingLead.name !== newName)) {
              await leads.update(existingLead.id, {
                name: newName,
                ...(subscriptionRes.emailId && { emailId: subscriptionRes.emailId }),
                ...(subscriptionRes.phoneNumber && { phoneNumber: subscriptionRes.phoneNumber }),
                ...(subscriptionRes.designation && { designation: subscriptionRes.designation }),
                ...(subscriptionRes.organisation && { organisation: subscriptionRes.organisation }),
              });
            } else if (!isValidNewName && currentNameIsInvalid) {
              // Only update other fields if name is invalid, never overwrite valid name with invalid
              await leads.update(existingLead.id, {
                ...(subscriptionRes.emailId && { emailId: subscriptionRes.emailId }),
                ...(subscriptionRes.phoneNumber && { phoneNumber: subscriptionRes.phoneNumber }),
                ...(subscriptionRes.designation && { designation: subscriptionRes.designation }),
                ...(subscriptionRes.organisation && { organisation: subscriptionRes.organisation }),
              });
            }
          } else if (
            (subscriptionRes.bookingId || subscriptionRes.emailId) &&
            fetchAttendeeDetailsFromDB.length === 0
          ) {
            // Only create if we truly didn't find the lead
            // This handles cases where subscription comes before our save completes
            // But we should rarely hit this for manual entries since we save immediately
            let stringifyObjArr = subscriptionRes["comment"];
            if (
              subscriptionRes["comment"] &&
              subscriptionRes["comment"] !== "undefined"
            ) {
              stringifyObjArr = subscriptionRes["comment"].map((obj) =>
                safeJSONStringify(obj)
              );
            }

            // Only create if name is valid, or use emailId/bookingId as fallback name
            // For manual leads, name might be empty but bookingId contains the email
            const subscriptionName = subscriptionRes.name?.trim();
            const isValidName = subscriptionName && subscriptionName !== "" && subscriptionName.toLowerCase() !== "unknown";
            // If name is empty but bookingId is an email, use bookingId as name
            let leadName = isValidName ? subscriptionName : "";
            if (!leadName && subscriptionRes.bookingId && validEmailRegex.test(subscriptionRes.bookingId)) {
              leadName = subscriptionRes.bookingId;
            }
            // Final fallback
            if (!leadName) {
              leadName = subscriptionRes.emailId || subscriptionRes.bookingId || "";
            }
            
            // Use put instead of add to handle potential duplicates gracefully
            try {
              await leads.put({
                id: subscriptionRes.bookingId
                  ? subscriptionRes.bookingId.toLowerCase()
                  : subscriptionRes.emailId
                  ? subscriptionRes.emailId
                  : subscriptionRes,
                name: leadName,
                emailId: subscriptionRes.emailId ? subscriptionRes.emailId : "",
                phoneNumber: subscriptionRes.phoneNumber
                  ? subscriptionRes.phoneNumber
                  : "",
                bookingId: subscriptionRes.bookingId
                  ? subscriptionRes.bookingId.toLowerCase()
                  : subscriptionRes.emailId
                  ? subscriptionRes.emailId
                  : subscriptionRes,
                designation: subscriptionRes.designation
                  ? subscriptionRes.designation
                  : "",
                organisation: subscriptionRes.organisation
                  ? subscriptionRes.organisation
                  : "",
                createdAt: subscriptionRes.createdAt,
                eventId: subscriptionRes.eventId,
                sponsorCode: subscriptionRes.sponsorCode,
                network: "",
                createNetwork: "",
                comment: stringifyObjArr,
                scannedBy: subscriptionRes.scannedBy,
                status: subscriptionRes.status,
                detCode: subscriptionRes.detCode ?? "",
              });
            } catch (err) {
              // If put fails, lead might already exist - that's okay, skip
              console.log("Subscription: Lead already exists or error creating:", err);
            }
          }
        }
      },
      error: (error) => console.warn(error),
    });
    return () => {
      createSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (sponsor_code) {
      localStorage.setItem("sponsorCode", sponsor_code);
    }
  }, [sponsor_code]);

  // ===== IndexedDB Queries =====
  const allItems = useLiveQuery(async () => await leads.toArray());

  const offlineData = allItems?.filter((singleDataSet) => {
    return singleDataSet.network === "false";
  });

  // ===== API Functions =====
  const fetchEventDetails = async () => {
    let fetchEventDetailsFromDB = await events
      .where("eventId")
      .equals(event_id)
      .toArray();

    setEventName(fetchEventDetailsFromDB[0]?.eventName || "");
    setEventDate(fetchEventDetailsFromDB[0]?.startDate || "");
    setEventDetails(fetchEventDetailsFromDB);
    handleClickAdvanced();
  };

  const getSponsorLeadsApi = async (token) => {
    let sponsorCode = sponsor_code
      ? sponsor_code
      : localStorage.getItem("sponsorCode");
    await getSponsorLeads(sponsorCode, token)
      .then(async (res) => {
        let allAttendeeDetails = await res.data.getSponsorLeads;
        let parsedData = safeJSONParse(allAttendeeDetails);
        let nextToken = parsedData.nextToken;
        let leadsData = parsedData.items;
        data = [...data, ...leadsData];
        if (nextToken !== null) {
          getSponsorLeadsApi(nextToken);
          loop = true;
        } else {
          if (loop) {
            postAPIFunctions(data);
          } else {
            postAPIFunctions(leadsData);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const postAPIFunctions = async (allLeadDetails) => {
    // Get existing manually saved leads before clearing to preserve their flags
    const existingLeads = await leads.toArray();
    const manuallySavedLeads = new Map();
    existingLeads.forEach(lead => {
      if (lead._manualSave === true) {
        manuallySavedLeads.set(lead.id, {
          _manualSave: lead._manualSave,
          _savedAt: lead._savedAt
        });
      }
    });

    leads.clear();
    allLeadDetails.forEach((entry) => {
      let stringifyObjArr = entry["comment"];
      if (entry["comment"] && entry["comment"] !== "undefined") {
        stringifyObjArr = entry["comment"].map((obj) => safeJSONStringify(obj));
      }

      // For manual leads, name might be empty but bookingId contains the email
      // Use bookingId as name if name is empty and bookingId looks like an email
      let leadName = entry["name"]?.trim() || "";
      if (!leadName && entry["bookingId"] && validEmailRegex.test(entry["bookingId"])) {
        leadName = entry["bookingId"];
      }
      // If still empty, use bookingId as fallback
      if (!leadName) {
        leadName = entry["bookingId"] || "";
      }

      const leadId = entry["bookingId"];
      const manualSaveData = manuallySavedLeads.get(leadId);

      leadList.push({
        id: leadId,
        name: leadName,
        emailId: entry["emailId"] || (validEmailRegex.test(entry["bookingId"]) ? entry["bookingId"] : ""),
        phoneNumber: entry["phoneNumber"],
        bookingId: entry["bookingId"],
        designation: entry["designation"],
        organisation: entry["organisation"],
        createdAt: entry["createdAt"],
        status: entry["status"],
        eventId: entry["eventId"],
        sponsorCode: entry["sponsorCode"],
        network: "",
        createNetwork: "",
        comment: stringifyObjArr,
        scannedBy: entry["scannedBy"],
        detCode: entry["detCode"],
        // Preserve _manualSave flag if this lead was manually saved
        ...(manualSaveData && { 
          _manualSave: manualSaveData._manualSave,
          _savedAt: manualSaveData._savedAt
        }),
      });
    });

    leads
      .bulkAdd(leadList)
      .then(() => {
        console.log("Done adding all leads");
      })
      .catch(Dexie.BulkError, (e) => {
        console.error("Some leads did not succeed");
      });
  };

  const crateLeadInIndexDB = async (leadData) => {
    // Use put instead of add to handle duplicates gracefully
    // This prevents errors if lead already exists
    try {
      await leads.put(leadData);
    } catch (err) {
      // If put fails, try to update instead
      try {
        await leads.update(leadData.id, leadData);
      } catch (updateErr) {
        console.error("Error saving lead to IndexedDB:", updateErr);
      }
    }
  };

  const editLeadIndexedDB = async (data) => {
    await leads.update(data.key, {
      comment: data.comment,
      network: data.network.toString(),
      createNetwork: data.createNetwork ? data.createNetwork.toString() : "",
    });
  };

  // ===== Save Lead Immediately on Scan (without comment) =====
  const saveLeadOnScan = async (entry) => {
    let name = "";
    let sponsorCode = localStorage.getItem("sponsorCode");
    
    if (typeof entry === "object") {
      name = entry["n"]
        ? entry["n"]
        : entry["id"]
        ? entry["id"]
        : entry["email"]
        ? entry["email"]
        : "";
    } else {
      name = entry;
    }

    const bookingId = typeof entry === "object"
      ? (entry["id"]
          ? entry["id"].toLowerCase()
          : entry["email"]
          ? entry["email"]
          : entry)
      : (typeof entry === "string" ? entry.toLowerCase() : entry);

    // Check if lead already exists
    const existingLead = await findDuplicate(bookingId);
    if (existingLead) {
      // Lead already exists, don't save again
      return;
    }

    if (networkStatus) {
      // Online: Save to API and IndexedDB
      const leadForApi = {
        bookingId: bookingId,
        comment: "",
        eventId: event_id,
        name: name,
        sponsorCode: sponsorCode,
        scannedBy: profileName,
        status: 1,
        ...(typeof entry === "object" && entry.designation && { designation: entry.designation }),
        ...(typeof entry === "object" && entry.organisation && { organisation: entry.organisation }),
        ...(typeof entry === "object" && entry["email"] && { emailId: entry["email"] }),
        ...(typeof entry === "object" && entry["phno"] && { phoneNumber: entry["phno"] }),
        ...(typeof entry === "object" && entry.detCode && { detCode: entry.detCode }),
        ...(typeof entry === "string" && validEmailRegex.test(entry) && { emailId: entry })
      };

      try {
        const res = await addSponsorLead([leadForApi]);
        const parsedData = safeJSONParse(res.data.addSponsorLeads);
        if (parsedData.statusCode === 200) {
          const responseData = parsedData.body?.data || parsedData.body || [];
          const responseLead = Array.isArray(responseData) ? responseData[0] : responseData;
          
          // Ensure we have a valid name - prefer API response, fallback to entry (email or bookingId)
          const apiName = responseLead?.name?.trim();
          const isValidApiName = apiName && apiName !== "" && apiName.toLowerCase() !== "unknown";
          // For manual entries, always use a valid name - email or bookingId, never empty
          const finalName = isValidApiName ? apiName : (name?.trim() || bookingId || "Manual Entry");
          
          const leadForIndexedDB = {
            id: bookingId,
            name: finalName,
            emailId: typeof entry === "object" && entry["email"] ? entry["email"] : (typeof entry === "string" && validEmailRegex.test(entry) ? entry : ""),
            phoneNumber: typeof entry === "object" && entry["phno"] ? entry["phno"] : "",
            bookingId: bookingId,
            designation: typeof entry === "object" && entry.designation ? entry.designation : "",
            organisation: typeof entry === "object" && entry.organisation ? entry.organisation : "",
            status: 1,
            eventId: event_id,
            sponsorCode: sponsorCode,
            network: "true",
            createNetwork: "true",
            comment: "",
            scannedBy: profileName,
            _manualSave: true, // Flag to identify manually saved leads
            _savedAt: Date.now(), // Timestamp to prevent subscription interference
            ...(typeof entry === "object" && entry.detCode && { detCode: entry.detCode }),
            ...(typeof entry === "string" && !validEmailRegex.test(entry) && { detCode: entry }),
          };

          // Use put instead of add to handle duplicates gracefully
          // This ensures if subscription tries to create same lead, it just updates
          await leads.put(leadForIndexedDB);
        }
      } catch (err) {
        console.error("Error saving lead on scan:", err);
        // If API fails, save offline - ensure name is valid
        const offlineName = name?.trim() || bookingId || "Manual Entry";
        await crateLeadInIndexDB({
          id: bookingId,
          name: offlineName,
          emailId: typeof entry === "object" && entry["email"] ? entry["email"] : (typeof entry === "string" && validEmailRegex.test(entry) ? entry : ""),
          phoneNumber: typeof entry === "object" && entry["phno"] ? entry["phno"] : "",
          bookingId: bookingId,
          designation: typeof entry === "object" && entry.designation ? entry.designation : "",
          organisation: typeof entry === "object" && entry.organisation ? entry.organisation : "",
          status: 1,
          eventId: event_id,
          sponsorCode: sponsor_code,
          network: "false",
          createNetwork: "false",
          comment: "undefined",
          scannedBy: profileName,
          _manualSave: true, // Flag to identify manually saved leads
          _savedAt: Date.now(), // Timestamp to prevent subscription interference
          ...(typeof entry === "object" && entry.detCode && { detCode: entry.detCode }),
          ...(typeof entry === "string" && !validEmailRegex.test(entry) && { detCode: entry }),
        });
      }
    } else {
      // Offline: Save to IndexedDB only - ensure name is valid
      const offlineName = name?.trim() || bookingId || "Manual Entry";
      await crateLeadInIndexDB({
        id: bookingId,
        name: offlineName,
        emailId: typeof entry === "object" && entry["email"] ? entry["email"] : (typeof entry === "string" && validEmailRegex.test(entry) ? entry : ""),
        phoneNumber: typeof entry === "object" && entry["phno"] ? entry["phno"] : "",
        bookingId: bookingId,
        designation: typeof entry === "object" && entry.designation ? entry.designation : "",
        organisation: typeof entry === "object" && entry.organisation ? entry.organisation : "",
        status: 1,
        eventId: event_id,
        sponsorCode: sponsor_code,
        network: "false",
        createNetwork: "false",
        comment: "undefined",
        scannedBy: profileName,
        _manualSave: true, // Flag to identify manually saved leads
        _savedAt: Date.now(), // Timestamp to prevent subscription interference
        ...(typeof entry === "object" && entry.detCode && { detCode: entry.detCode }),
        ...(typeof entry === "string" && !validEmailRegex.test(entry) && { detCode: entry }),
      });
    }
  };

  // ===== Update Lead Comment Only =====
  const updateLeadCommentOnly = async (entry) => {
    if (!commentedText || commentedText.trim() === "") {
      // No comment to save, just close the modal
      navigate(-1);
      if (typeof entry === "object") {
        handleClickAdvanced();
      }
      return;
    }

    const bookingId = typeof entry === "object"
      ? (entry["id"] ? entry["id"].toLowerCase() : entry["email"] ? entry["email"] : entry)
      : entry.toLowerCase();

    const currentTime = new Date().toISOString();
    const commentObject = {
      commentedBy: profileName,
      comment: commentedText,
      commentedAt: currentTime,
      isOnline: networkStatus,
    };

    // Get existing lead from IndexedDB
    const existingLead = await leads.get(bookingId);
    
    if (!existingLead) {
      console.error("Lead not found for comment update");
      navigate(-1);
      return;
    }

    // Get existing comments
    let existingComments = [];
    if (existingLead.comment && existingLead.comment !== "undefined" && Array.isArray(existingLead.comment)) {
      existingComments = existingLead.comment;
    }

    // Add new comment
    const updatedComments = [...existingComments, safeJSONStringify(commentObject)];

    if (networkStatus) {
      // Online: Update via API and IndexedDB
      try {
        // Get sponsorCode from localStorage or URL param
        const sponsorCode = sponsor_code || localStorage.getItem("sponsorCode");
        
        // Validate required fields
        if (!bookingId || !event_id || !sponsorCode) {
          console.error("Missing required fields for update:", { bookingId, event_id, sponsorCode });
          // Still update IndexedDB for offline sync
          await editLeadIndexedDB({
            key: bookingId,
            comment: updatedComments,
            network: existingLead.network || "false",
            createNetwork: existingLead.createNetwork || "false",
          });
          navigate(-1);
          if (typeof entry === "object") {
            handleClickAdvanced();
          }
          return;
        }
        
        // Format comment for API - send only the new comment as a stringified object
        // The API expects a single stringified comment object when updating
        // (matching the format used in EditCommentModal)
        const commentString = safeJSONStringify(commentObject);
        
        const updateData = [{
          bookingId: String(bookingId || ""),
          eventId: String(event_id || ""),
          sponsorCode: String(sponsorCode || ""),
          comment: commentString,
        }];
        await updateSponsorLead(updateData);

        // Update IndexedDB
        await editLeadIndexedDB({
          key: bookingId,
          comment: updatedComments,
          network: existingLead.network || "true",
          createNetwork: existingLead.createNetwork || "true",
        });
      } catch (err) {
        console.error("Error updating comment:", err);
        // Still update IndexedDB for offline sync
        await editLeadIndexedDB({
          key: bookingId,
          comment: updatedComments,
          network: existingLead.network || "false",
          createNetwork: existingLead.createNetwork || "false",
        });
      }
    } else {
      // Offline: Update IndexedDB only
      await editLeadIndexedDB({
        key: bookingId,
        comment: updatedComments,
        network: existingLead.network || "false",
        createNetwork: existingLead.createNetwork || "false",
      });
    }

    navigate(-1);
    if (typeof entry === "object") {
      handleClickAdvanced();
    }
  };

  // ===== QR Scan Handler =====
  const handleScanResult = async (result) => {
    try {
      setDisplay(true);

      if (!result) return;

      let fetchEventDetailsFromDB = await events
        .where("eventId")
        .equals(event_id)
        .toArray();
      const decodedEntry = decodeTextFormatter(result[0].rawValue);

      if (
        fetchEventDetailsFromDB[0]?.city?.toLowerCase()?.includes(Dubai) &&
        !decodedEntry.id
      ) {
        const detCode = result[0].rawValue;

        const findAttendeeFromIndexDB = await leads
          .where("detCode")
          .equals(detCode)
          .first();

        if (findAttendeeFromIndexDB) {
          setDuplicateLeadModal(true);
          setResult(findAttendeeFromIndexDB);
          navigate({
            pathname: `/events/${event_id}/scanDetails/DetailsCardModal`,
            search: `?bookingId=${findAttendeeFromIndexDB.bookingId}`,
          });
        } else {
          setResult(detCode);
          // Save lead immediately on scan (without comment)
          await saveLeadOnScan(detCode);
          navigate({
            pathname: `/events/${event_id}/scanDetails/ScanLeadModal`,
            search: `?bookingId=${detCode}`,
          });
        }
      } else {
        setDuplicateLeadModal(false);

        const duplicateEntry = await findDuplicate(
          decodedEntry["id"] ? decodedEntry["id"] : decodedEntry["email"]
        );

        if (decodedEntry["eid"] && decodedEntry["eid"] !== event_id) {
          setInvalidEventModal(true);
        } else if (!decodedEntry["eid"]) {
          setInvalidQrCode(true);
        } else if (decodedEntry["id"] && decodedEntry["id"].includes(",")) {
          let SingleBuyerLeadIdArr = decodedEntry["id"].split(",");
          let duplicateEntryCheck = await findDuplicate(SingleBuyerLeadIdArr[0]);

          if (duplicateEntryCheck) {
            setDuplicateLeadModal(true);
            setResult(duplicateEntryCheck);
            navigate({
              pathname: `/events/${event_id}/scanDetails/DetailsCardModal`,
              search: `?bookingId=${SingleBuyerLeadIdArr[0]}`,
            });
          } else {
            setResult(decodedEntry);
            // Save lead immediately on scan (without comment)
            await saveLeadOnScan(decodedEntry);
            navigate({
              pathname: `/events/${event_id}/scanDetails/ScanLeadModal`,
              search: `?bookingId=${decodedEntry["id"]}`,
            });
          }
        } else if (duplicateEntry) {
          setResult(duplicateEntry);
          setDuplicateLeadModal(true);
          navigate({
            pathname: `/events/${event_id}/scanDetails/DetailsCardModal`,
            search: `?bookingId=${decodedEntry["id"]}`,
          });
        } else if (decodedEntry["eid"] && decodedEntry["eid"] === event_id) {
          setResult(decodedEntry);
          // Save lead immediately on scan (without comment)
          await saveLeadOnScan(decodedEntry);
          navigate({
            pathname: `/events/${event_id}/scanDetails/ScanLeadModal`,
            search: `?bookingId=${decodedEntry["id"]}`,
          });
        }
      }
    } catch (error) {
      console.error("Error in handleScanResult:", error);
    }
  };

  const updateLeadApi = async (objectOfLeadDetials) => {
    try {
      updateSponsorLead(objectOfLeadDetials);
    } catch (err) {
      console.log(err);
    }
  };

  async function onCreate(entry) {
    let name = "";
    let arrOfLeads = [];
    let leadListLocal = [];
    let leadListForApi = [];
    let sponsorCode = localStorage.getItem("sponsorCode");
    if (typeof entry === "object") {
      name = entry["n"]
        ? entry["n"]
        : entry["id"]
        ? entry["id"]
        : entry["email"]
        ? entry["email"]
        : "";
    } else {
      name = entry;
    }

    let commentObject = {
      commentedBy: profileName,
      comment: commentedText,
      commentedAt: currentTime,
      isOnline: networkStatus,
    };
    if (entry["id"]?.includes(",")) {
      arrOfLeads = entry["id"]?.split(",");
    } else {
      arrOfLeads.push(
        entry["id"]
          ? entry["id"].toLowerCase()
          : entry["email"]
          ? entry["email"]
          : entry
      );
    }

    if (networkStatus) {
      arrOfLeads.forEach((singleLeadId) => {
        leadListLocal.push({
          id: singleLeadId,
          name: name,
          emailId: entry["email"] ? entry["email"] : "",
          phoneNumber: entry["phno"] ? entry["phno"] : "",
          bookingId: entry["id"]
            ? entry["id"].toLowerCase()
            : entry["email"]
            ? entry["email"]
            : entry,
          designation: entry.designation ? entry.designation : "",
          organisation: entry.organisation ? entry.organisation : "",
          status: 1,
          eventId: event_id,
          sponsorCode: sponsorCode,
          network: "true",
          createNetwork: "true",
          ...(commentedText
            ? { comment: [safeJSONStringify(commentObject)] }
            : { comment: "" }),
          scannedBy: profileName,
          _manualSave: true, // Flag to identify manually saved leads
          _savedAt: Date.now(), // Timestamp to prevent subscription interference
        });

        leadListForApi.push({
          bookingId: singleLeadId,
          ...(commentedText
            ? { comment: safeJSONStringify(commentObject) }
            : { comment: "" }),
          eventId: event_id,
          name: name,
          sponsorCode: sponsorCode,
          ...(entry.designation && { designation: entry.designation }),
          ...(entry.organisation && { organisation: entry.organisation }),
          ...(entry["email"] && { emailId: entry["email"] }),
          ...(entry["phno"] && { phoneNumber: entry["phno"] }),
          scannedBy: profileName,
          status: 1,
          ...(entry.detCode && { detCode: entry.detCode }),
        });
      });
      try {
        const res = await addSponsorLead(leadListForApi);
        const parsedData = safeJSONParse(res.data.addSponsorLeads);
        if (parsedData.statusCode === 200) {
          // Check if API response contains lead data with actual names
          const responseData = parsedData.body?.data || parsedData.body || [];
          const responseLeads = Array.isArray(responseData) ? responseData : [responseData];

          // Update leadListLocal with actual names from API response if available
          leadListLocal.forEach((localLead, index) => {
            const apiLead = responseLeads.find(l => l.bookingId === localLead.bookingId || l.bookingId === localLead.id);
            if (apiLead && apiLead.name && apiLead.name !== localLead.name) {
              localLead.name = apiLead.name;
              // Also update other fields if available
              if (apiLead.emailId) localLead.emailId = apiLead.emailId;
              if (apiLead.phoneNumber) localLead.phoneNumber = apiLead.phoneNumber;
              if (apiLead.designation) localLead.designation = apiLead.designation;
              if (apiLead.organisation) localLead.organisation = apiLead.organisation;
            }
          });

          leads
            .bulkAdd(leadListLocal)
            .then(() => {
              console.log("added leads to indexedDB");
            })
            .catch(Dexie.BulkError, (e) => {
              console.error("Some leads did not succeed");
            });
          navigate(-1);
          if (typeof entry === "object") {
            handleClickAdvanced();
          }
        } else {
          console.log(res);
          showError("Something went wrong");
          navigate(-1);
        }
      } catch (err) {
        console.log(err);
        throw err; // Re-throw to allow modal to handle error
      }
    } else {
      try {
        await crateLeadInIndexDB({
          id: entry["id"]
            ? entry["id"].toLowerCase()
            : entry["email"]
            ? entry["email"]
            : entry,
          name: name,
          emailId: entry["email"] ? entry["email"] : "",
          phoneNumber: entry["phno"] ? entry["phno"] : "",
          bookingId: entry["id"]
            ? entry["id"].toLowerCase()
            : entry["email"]
            ? entry["email"]
            : entry,
          designation: entry.designation ? entry.designation : "",
          organisation: entry.organisation ? entry.organisation : "",
          status: 1,
          eventId: event_id,
          sponsorCode: sponsor_code,
          network: "false",
          createNetwork: "false",
          ...(commentedText
            ? { comment: [safeJSONStringify(commentObject)] }
            : { comment: "undefined" }),
          scannedBy: profileName,
          _manualSave: true, // Flag to identify manually saved leads
          _savedAt: Date.now(), // Timestamp to prevent subscription interference
          ...(entry.detCode && { detCode: entry.detCode }),
        });
        navigate(-1);
        if (typeof entry === "object") {
          handleClickAdvanced();
        }
      } catch (err) {
        console.error("Error saving lead offline:", err);
        throw err; // Re-throw to allow modal to handle error
      }
    }
  }

  const formattingOfflineComments = (singleDatasetComment) => {
    let parsedComments = singleDatasetComment.map((item) =>
      safeJSONParse(item)
    );
    let offlineComments = parsedComments.filter(
      (comment) => comment.isOnline === false
    );
    return offlineComments;
  };

  const formattingOnlineComments = (singleDatasetComment) => {
    let parsedComments = singleDatasetComment.map((item) =>
      safeJSONParse(item)
    );
    let onlineComments = parsedComments.filter(
      (comment) => comment.isOnline === true
    );
    return onlineComments;
  };

  const syncDetailsAPI = async () => {
    let arrOfLeadDetails = [];
    let createLeadDetailsArr = [];

    offlineData?.forEach((singleDataset) => {
      let emptyStringforComments = [];
      let isOnlineComments = [];
      let isPreviousOnlineComments = [];
      let indexedDBComments = [];
      var leadDetials = {};
      var createLeadDetials = {};

      if (singleDataset.createNetwork === "false") {
        if (!singleDataset.comment || singleDataset.comment === "undefined") {
          createLeadDetials = {
            bookingId: singleDataset.bookingId,
            eventId: event_id,
            sponsorCode: sponsor_code,
            comment:
              singleDataset.comment === "undefined"
                ? ""
                : singleDataset.comment,
            name: singleDataset.name,
            designation: singleDataset.designation
              ? singleDataset.designation
              : "",
            emailId: singleDataset.emailId ? singleDataset.emailId : "",
            phoneNumber: singleDataset.phoneNumber ? singleDataset.phoneNumber : "",
            organisation: singleDataset.organisation
              ? singleDataset.organisation
              : "",
            scannedBy: profileName,
            status: 1,
          };
          createLeadDetailsArr.push(createLeadDetials);

          editLeadIndexedDB({
            key: singleDataset.bookingId,
            network: true,
            comment: singleDataset.comment,
            createNetwork: true,
          });
        }
      }
      if (singleDataset.comment && singleDataset.comment !== "undefined") {
        let arrOfOfflineComments = formattingOfflineComments(
          singleDataset.comment
        );
        if (arrOfOfflineComments.length > 0) {
          arrOfOfflineComments.forEach((singleComment) => {
            let obj = { ...singleComment, isOnline: true };
            emptyStringforComments.push(safeJSONStringify(obj));
          });

          if (singleDataset.createNetwork === "false") {
            createLeadDetials = {
              bookingId: singleDataset.bookingId,
              eventId: event_id,
              sponsorCode: sponsor_code,
              comment: emptyStringforComments,
              name: singleDataset.name,
              designation: singleDataset.designation
                ? singleDataset.designation
                : "",
              emailId: singleDataset.emailId ? singleDataset.emailId : "",
              phoneNumber: singleDataset.phoneNumber ? singleDataset.phoneNumber : "",
              organisation: singleDataset.organisation
                ? singleDataset.organisation
                : "",
              scannedBy: profileName,
              status: 1,
            };
            createLeadDetailsArr.push(createLeadDetials);
          } else {
            leadDetials = {
              bookingId: singleDataset.bookingId,
              eventId: event_id,
              sponsorCode: sponsor_code,
              comment: emptyStringforComments,
            };
            arrOfLeadDetails.push(leadDetials);
          }

          let arrOfOnlineComments = formattingOnlineComments(
            singleDataset.comment
          );

          if (arrOfOnlineComments && arrOfOfflineComments) {
            arrOfOnlineComments.forEach((item) => {
              isPreviousOnlineComments.push(safeJSONStringify(item));
            });
            arrOfOfflineComments.forEach((singleComment) => {
              let obj = { ...singleComment, isOnline: true };
              isOnlineComments.push(safeJSONStringify(obj));
            });
            indexedDBComments = [
              ...isPreviousOnlineComments,
              ...isOnlineComments,
            ];
          } else if (arrOfOnlineComments) {
            indexedDBComments = [...isPreviousOnlineComments];
          }
          editLeadIndexedDB({
            key: singleDataset.bookingId,
            network: true,
            comment: indexedDBComments,
            ...(singleDataset.createNetwork && { createNetwork: true }),
          });
        }
      }
    });

    if (offlineData) {
      if (createLeadDetailsArr.length !== 0) {
        try {
          await addSponsorLead(createLeadDetailsArr);
          createLeadDetailsArr = [];
        } catch (err) {
          console.log(err);
        }
      }
      if (arrOfLeadDetails.length !== 0) {
        updateLeadApi(arrOfLeadDetails);
        arrOfLeadDetails = [];
      }
    }
  };

  const findDuplicate = async (entity_id) => {
    if (entity_id) {
      let findLeadFromIndexDB = await leads.get(entity_id);
      if (findLeadFromIndexDB) {
        return findLeadFromIndexDB;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  // ===== Event Handlers =====
  const SubmitButton = async () => {
    try {
      if (booking) {
        // Trim the booking value - if it's an email, trim all spaces; if booking ID, trim leading spaces
        let trimmedBooking = booking;
        if (booking.includes("@")) {
          // It's an email, trim all leading and trailing spaces
          trimmedBooking = booking.trim();
        } else {
          // It's a booking ID, only trim leading spaces
          trimmedBooking = booking.replace(/^\s+/, "");
        }
        
        // For manual entry, save lead first if it doesn't exist, then update comment
        const bookingId = trimmedBooking.toLowerCase();
        const existingLead = await findDuplicate(bookingId);
        if (!existingLead) {
          // Lead doesn't exist, save it first (without comment)
          await saveLeadOnScan(trimmedBooking);
        }
        // Update comment on the lead
        await updateLeadCommentOnly(trimmedBooking);
      } else {
        // Update comment on the scanned lead
        await updateLeadCommentOnly(result);
      }
      setBooking("");
      setCommentedText("");
    } catch (error) {
      console.error("Error in SubmitButton:", error);
      throw error;
    }
  };

  const bookingClickHandler = async () => {
    setDuplicateLeadModal(false);
    const validDETRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

    // Trim the booking value - if it's an email, trim all spaces; if booking ID, trim leading spaces
    let trimmedBooking = booking;
    if (booking.includes("@")) {
      // It's an email, trim all leading and trailing spaces
      trimmedBooking = booking.trim();
    } else {
      // It's a booking ID, only trim leading spaces
      trimmedBooking = booking.replace(/^\s+/, "");
    }

    if (
      trimmedBooking.length === 8 ||
      trimmedBooking.match(validEmailRegex) ||
      trimmedBooking.match(validDETRegex)
    ) {
      let duplicateBoolean = await findDuplicate(trimmedBooking);

      if (duplicateBoolean) {
        setError("Entered booking Id/ Email Id has been already captured");
      } else {
        setError("");
        const bookingLower = trimmedBooking.toLowerCase();
        setBooking(bookingLower);
        // Save lead immediately on manual entry (without comment)
        await saveLeadOnScan(bookingLower);
        navigate({
          pathname: `/events/${event_id}/scanDetails/ScanLeadModal`,
          search: `?bookingId=${bookingLower}`,
        });
      }
    } else {
      setError("Please enter a valid booking ID/Email ID");
    }
  };

  const handleStop = () => {
    setDisplay(true);
    setCancel(false);
  };

  const decodeTextFormatter = (decodeText) => {
    let entryText = "";
    let entry = "";
    if (decodeText.includes("\n")) {
      entryText = `{"${decodeText
        .replaceAll("\n", '","')
        .replaceAll(": ", '":"')}"}`;
      entry = { ...safeJSONParse(entryText) };
      entry["n"] = entry["Name"];
      delete entry["Name"];
      entry["id"] = entry["Booking Id"];
      delete entry["Booking Id"];
      entry["email"] = entry["Email Id"];
      delete entry["Email Id"];
      entry["phno"] = entry["Phone Number"];
      delete entry["Phone Number"];
      return entry;
    } else if (decodeText.includes("|")) {
      entryText = `{"${decodeText
        .replaceAll("|", '","')
        .replaceAll(":", '":"')}"}`;
      entry = { ...safeJSONParse(entryText) };
      return entry;
    } else {
      return decodeText;
    }
  };

  const handleClickAdvanced = () => {
    setResult("");
    setDisplay(false);
    setCancel(true);
    setBooking("");
    setCameraError(false);
    // Force scanner remount by incrementing key
    setScannerKey(prev => prev + 1);
  };

  const bookingOrEmailChangeHandler = (e) => {
    let value = e.target.value;
    
    // Always trim leading spaces immediately (for both emails and booking IDs)
    // This handles pasting with leading spaces or adding spaces at the start
    value = value.replace(/^\s+/, "");
    
    setBooking(value);
    setError("");
  };

  const bookingOrEmailBlurHandler = (e) => {
    let value = e.target.value;

    // On blur, if it's an email, trim trailing spaces as well
    // This ensures emails are properly trimmed when user leaves the field
    if (value.includes("@")) {
      value = value.trim();
      setBooking(value);
    }
  };

  // Scroll focused input into view when keyboard opens (mobile)
  const handleInputFocus = (e) => {
    const element = e.target;
    // Multiple delays to handle different keyboard animation speeds
    // First attempt after 100ms, second after 300ms for slower keyboards
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
  };

  const commentBoxChangeHandler = (e) => {
    setCommentedText(e.target.value);
  };

  const shareClickHandler = () => {
    navigate({ pathname: `/events/${event_id}/scanDetails/ShareSponsorCode` });
  };

  const closeScanModal = () => {
    handleClickAdvanced();
  };

  const cameraErrorHandling = (error) => {
    if (error.name === "NotAllowedError") {
      if (error.message.includes("Permission dismissed")) {
        setCameraError("Permission dismissed");
      } else if (error.message.includes("Permission denied")) {
        setCameraError("Permission denied");
      }
    } else {
      setCameraError(error.message);
    }
  };

  // ===== Navigation Handlers =====
  const onClickHome = () => navigate({ pathname: "/events" });
  const onClickLeads = () =>
    navigate({
      pathname: `/events/${event_id}/view-leads`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickTeam = () =>
    navigate({
      pathname: `/events/${event_id}/team`,
      search: `?sponsor=${sponsor_code}`,
    });
  const onClickAddEvent = () => navigate({ pathname: "/addEventModal" });

  // Note: handleAddToHomeScreen is now handled directly in MenuActionsModal component

  // ===== Format Date =====
  const formatEventDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ===== Render =====
  return (
    <div
      className={`scan-page ${refreshDisabled ? 'scan-page--offline' : ''} ${shouldApplyKeyboardLayout ? 'scan-page--keyboard-open' : ''} ${isAndroidKeyboardOpen && !shouldIgnoreKeyboardHide ? 'scan-page--android-keyboard' : ''}`}
      style={shouldApplyKeyboardLayout && !isDesktop ? {
        height: '100%',
        minHeight: '100%',
        maxHeight: '100%'
      } : undefined}
    >
      {/* Chrome Warning Banner (Desktop Only) */}
      <ChromeWarningBanner />

      {/* Mobile Header - Show on mobile or iPad (even in desktop mode) */}
      <header className={`scan-header ${isIPad ? 'scan-header--ipad' : ''}`}>
        <div className="scan-header__left">
          <button className="scan-header__menu-btn" onClick={() => setMenuModalOpen(true)}>
            <Icon icon="solar:hamburger-menu-broken" width="24" height="24" />
          </button>
          <h1 className="scan-header__event-name">{eventName || "Event"}</h1>
        </div>
        <div className="scan-header__right">
          <button className="scan-header__theme-btn" onClick={toggleTheme}>
            <Icon
              icon={theme === "dark" ? "mdi:weather-sunny" : "mdi:weather-night"}
              width="24"
              height="24"
              color="currentColor"
              style={{ display: 'block' }}
            />
          </button>
          <Avatar
            src={Context?.state?.user?.attributes?.picture}
            name={Context?.state?.user?.attributes?.name}
            size={36}
            onClick={() => setProfileModalOpen(true)}
            className="scan-header__avatar"
          />
        </div>
      </header>

      {/* Offline Banner */}
      {refreshDisabled && (
        <div className="scan-offline-banner">
          <div className="scan-offline-banner__content">
            <div className="scan-offline-banner__icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 5.52086L1.27933 6.80019C1.73533 6.34419 2.23114 5.94067 2.74686 5.57062L1.45395 4.27771C0.947286 4.65952 0.455095 5.06486 0 5.52086ZM4.68757 2.50348L2.1841 0L0.904762 1.27933L17.1905 17.5651L18.4698 16.2857L13.9351 11.751L14.8182 10.8689C13.805 9.86361 12.5128 9.18644 11.1096 8.92548L9.04852 6.86443C11.5719 6.69162 14.1595 7.54119 16.0414 9.42219L17.3208 8.14286C14.6988 5.51995 10.9322 4.58171 7.46971 5.28562L6.08995 3.90586C7.25436 3.54147 8.4672 3.3554 9.68729 3.35395C12.8874 3.35395 15.8731 4.5781 18.0952 6.80019L19.3746 5.52086C16.8114 2.95676 13.3706 1.54443 9.68729 1.54443C7.97555 1.55013 6.27992 1.87538 4.68757 2.50348ZM2.05381 8.14286L3.33314 9.42219C3.88765 8.87256 4.514 8.40051 5.19514 8.01891L3.8751 6.69886C3.22638 7.1069 2.61295 7.58462 2.05381 8.14286ZM4.55638 10.8698L5.83571 12.1491C6.46181 11.523 7.22452 11.1078 8.03338 10.8571L6.62467 9.44843C5.85547 9.79416 5.15485 10.2756 4.55638 10.8698ZM11.4869 14.3106L9.58686 12.4106C9.23842 12.4293 8.90285 12.5485 8.62074 12.7539C8.33863 12.9592 8.12205 13.2419 7.9972 13.5678C7.87234 13.8936 7.84456 14.2486 7.91721 14.5899C7.98987 14.9312 8.15984 15.2442 8.40657 15.4909C8.65331 15.7376 8.96625 15.9076 9.30754 15.9803C9.64883 16.0529 10.0039 16.0251 10.3297 15.9003C10.6555 15.7754 10.9382 15.5589 11.1436 15.2767C11.3489 14.9946 11.4682 14.6591 11.4869 14.3106Z" fill="white" stroke="white" strokeWidth="0.3"/>
              </svg>
            </div>
            <div className="scan-offline-banner__text">
              <span className="scan-offline-banner__title">You are offline.</span>
              <span className="scan-offline-banner__subtitle">All your leads will be synced once you are back online.</span>
            </div>
          </div>
          <button className="scan-offline-banner__close" onClick={() => setRefreshDisabled(false)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Desktop Header */}
      <DesktopHeader
        eventName={eventName}
        eventDate={eventDate}
        onShareClick={shareClickHandler}
        onHomeClick={onClickHome}
        onAddEventClick={onClickAddEvent}
        onProfileClick={() => setProfileModalOpen(true)}
        isDesktop={isDesktop}
      />

      {/* Desktop Secondary Nav */}
      <SecondaryNav
        activeTab="scan"
        onScanClick={() => {}}
        onLeadsClick={onClickLeads}
        onTeamClick={onClickTeam}
        isDesktop={isDesktop}
        showTeam={shouldShowTeamsFeature(eventDetails[0]?.createdAt)}
      />

      {/* Tabs (Mobile Only) */}
      {!isDesktop && (
        <div className="scan-tabs">
          <button
            className={`scan-tabs__tab ${activeTab === "scan" ? "scan-tabs__tab--active" : ""}`}
            onClick={() => setActiveTab("scan")}
          >
            Scan Leads
          </button>
          <button
            className={`scan-tabs__tab ${activeTab === "manual" ? "scan-tabs__tab--active" : ""}`}
            onClick={() => setActiveTab("manual")}
          >
            Enter Manually
          </button>
        </div>
      )}

      {/* Share Button (Mobile Only) */}
      {!isDesktop && (
        <div className="scan-share-btn" onClick={shareClickHandler}>
          <span className="scan-share-btn__pill">
            Share Event With Others
            <span className="scan-share-btn__icon">
              <Icon icon="mynaui:share" width="14" height="14" />
            </span>
          </span>
        </div>
      )}

      {/* Tabs and Content Card (Desktop) */}
      <div className="scan-card">
        {/* Desktop Tabs */}
        {isDesktop && (
          <div className="scan-tabs">
            <button
              className={`scan-tabs__tab ${activeTab === "scan" ? "scan-tabs__tab--active" : ""}`}
              onClick={() => setActiveTab("scan")}
            >
              Scan Leads
            </button>
            <button
              className={`scan-tabs__tab ${activeTab === "manual" ? "scan-tabs__tab--active" : ""}`}
              onClick={() => setActiveTab("manual")}
            >
              Enter Manually
            </button>
          </div>
        )}

        {/* Content */}
        <div className="scan-content">
        {activeTab === "scan" ? (
          <>
            {/* Scanner Area */}
            {!display && !cameraError && (
              <div className="scan-scanner-area">
                <div className="scan-qr-container">
                  <Scanner
                    key={scannerKey}
                    onScan={(result) => handleScanResult(result)}
                    components={{ zoom: true, torch: true }}
                    classNames={{ container: "scan-qr-scanner" }}
                    onError={(error) => cameraErrorHandling(error)}
                  />
                </div>
              </div>
            )}

            {/* Scanner Frame Placeholder */}
            {display && !cameraError && (
              <div className="scan-scanner-area">
                <div className="scan-scanner-frame">
                  <div className="scan-scanner-inner" />
                  <div className="scan-scanner-frame__corner-bl" />
                  <div className="scan-scanner-frame__corner-br" />
                </div>
              </div>
            )}

            {/* Camera Permission States */}
            {cameraError && (
              <div className="scan-camera-permission">
                <div className="scan-camera-permission__card">
                  <div className="scan-camera-permission__icon">
                    <Icon icon="tabler:camera-off" width="48" height="48" />
                  </div>
                  <h3 className="scan-camera-permission__title">
                    {cameraError === "Permission dismissed"
                      ? "Camera Permission Required"
                      : cameraError === "Permission denied"
                      ? "Camera Access Denied"
                      : "Camera Not Found"}
                  </h3>
                  <p className="scan-camera-permission__desc">
                    {cameraError === "Permission dismissed"
                      ? "Please allow camera access to scan QR codes."
                      : cameraError === "Permission denied"
                      ? "Camera access is blocked. Please enable it in your browser settings."
                      : "Please ensure your device has a camera connected."}
                  </p>
                  {cameraError === "Permission dismissed" && (
                    <button
                      className="scan-camera-permission__btn"
                      onClick={handleClickAdvanced}
                    >
                      Enable Camera
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Enter Manually Form */
          <div className="scan-manual-form">
            <h2 className="scan-manual-form__title">Add a new lead manually</h2>

            <label className="scan-manual-form__label">Booking ID/Email ID</label>
            <input
              id="bookingOrEmailInput"
              type="text"
              className="scan-manual-form__input"
              placeholder="Enter Booking ID/Email ID"
              value={booking}
              onChange={bookingOrEmailChangeHandler}
              onBlur={bookingOrEmailBlurHandler}
              onFocus={handleInputFocus}
            />
            {error && <p className="scan-manual-form__error">{error}</p>}

            <label className="scan-manual-form__label">Note</label>
            <textarea 
              id="commentBox"
              className="scan-manual-form__textarea"
              placeholder="Any notes or comments on the lead"
              value={commentedText}
              onChange={commentBoxChangeHandler}
              onFocus={handleInputFocus}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-1p-ignore
              data-lpignore="true"
            />

            <button
              className="scan-manual-form__btn"
              onClick={bookingClickHandler}
              disabled={!booking}
            >
              Add
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Bottom Navbar - Always render, CSS controls visibility for desktop/iPad */}
      {(!isKeyboardOpen && !isAndroidKeyboardOpen) || shouldIgnoreKeyboardHide ? (
        <BottomNavbar eventId={event_id} sponsorCode={sponsor_code} />
      ) : null}

      {/* Modals */}
      <InvalidEventModal
        showModal={invalidEventModal}
        setModal={setInvalidEventModal}
        onCloseModal={() => {
          setInvalidEventModal(false);
          handleClickAdvanced(); // Restart scanner after closing modal
        }}
      />

      <WarningModal
        showModal={invalidQrCode}
        setModal={setInvalidQrCode}
        onCloseModal={() => {
          setInvalidQrCode(false);
          handleClickAdvanced(); // Restart scanner after closing modal
        }}
        modalText="Invalid QR Code"
        modalSubtext="This attendee does not belong to this event"
      />

      <WarningModal
        showModal={cameraBlockedModal}
        setModal={setCameraBlockedModal}
        onCloseModal={() => setCameraBlockedModal(false)}
        modalText={
          <div>
            <div style={{ fontWeight: "500", fontSize: "20px", paddingBottom: "8px" }}>
              Allow Camera Access
            </div>
            <div>
              Camera access is disabled for this website. Please enable camera
              permissions in your browser settings.
            </div>
          </div>
        }
      />

      <Outlet
        context={{
          result: result,
          duplicateEntry: duplicateLeadModal,
          eventId: event_id,
          booking,
          commentedText,
          commentBoxChangeHandler: commentBoxChangeHandler,
          submitClickHandler: SubmitButton,
          closeScanModal,
        }}
      />

      {/* Menu Actions Modal */}
      <MenuActionsModal
        isOpen={menuModalOpen}
        onClose={() => setMenuModalOpen(false)}
        eventId={event_id}
        sponsorCode={sponsor_code}
        showTeam={shouldShowTeamsFeature(eventDetails[0]?.createdAt)}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </div>
  );
};

export default ScanDetails;
